/**
 * FusionCaseEntryModal — CryptoFlow-style New / Open case for real Fusion work.
 * Creates or selects an LE case, then navigates to /cases/:id/canvas (not the
 * Fusion Sandbox). Guest demos never open this path.
 */
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronDown, FolderOpen, Loader2, Plus, X } from 'lucide-react';
import { toast } from 'sonner';
import { caseService } from '../../services/api';
import { isGuestDemo } from '../../utils/guestDemo';
import { caseCanvasUrl } from '../../case-canvas/landingTab';

export type FusionCaseEntryMode = 'new' | 'open';

type Props = {
  open: boolean;
  initialMode?: FusionCaseEntryMode;
  onClose: () => void;
};

type CaseRow = { id: string; title: string; fir_number?: string };

/** A new case is empty, so it opens on Build; an existing case opens on Canvas. */
function canvasUrl(caseId: string, created: boolean) {
  return caseCanvasUrl(caseId, created ? 'build' : 'canvas');
}

export function FusionCaseEntryModal({ open, initialMode = 'new', onClose }: Props) {
  const navigate = useNavigate();
  const guest = isGuestDemo();
  const [mode, setMode] = useState<FusionCaseEntryMode>(initialMode);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [cases, setCases] = useState<CaseRow[]>([]);
  const [selectedId, setSelectedId] = useState('');
  const [listOpen, setListOpen] = useState(false);
  const listboxRef = useRef<HTMLDivElement>(null);
  const [form, setForm] = useState({
    title: '',
    fir_number: '',
    officer_name: '',
    notes: '',
  });

  useEffect(() => {
    if (!open) return;
    setMode(initialMode);
    setErr(null);
    setBusy(false);
    setSelectedId('');
    setListOpen(false);
    setForm({ title: '', fir_number: '', officer_name: '', notes: '' });
  }, [open, initialMode]);

  useEffect(() => {
    if (!open || guest || mode !== 'open') return;
    let cancelled = false;
    caseService.getAll().then((list: unknown) => {
      if (cancelled) return;
      const rows = Array.isArray(list) ? list : (list as { cases?: unknown[] })?.cases || [];
      setCases(
        (rows as Array<{ id?: string; case_id?: string; title?: string; fir_number?: string }>)
          .map((c) => ({
            id: String(c.id || c.case_id || ''),
            title: c.title || 'Untitled',
            fir_number: c.fir_number,
          }))
          .filter((c) => c.id),
      );
    }).catch(() => {
      if (!cancelled) setCases([]);
    });
    return () => { cancelled = true; };
  }, [open, guest, mode]);

  useEffect(() => {
    if (!listOpen) return;
    const onDoc = (e: MouseEvent) => {
      if (!listboxRef.current?.contains(e.target as Node)) setListOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setListOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [listOpen]);

  if (!open) return null;

  const goCanvas = (caseId: string, created = false) => {
    onClose();
    navigate(canvasUrl(caseId, created));
  };

  const onCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (guest) {
      setErr('Sign in to create a real Fusion case. Guests stay on the demo sandbox.');
      return;
    }
    const title = form.title.trim();
    if (!title) {
      setErr('Case title is required');
      return;
    }
    setBusy(true);
    setErr(null);
    try {
      const created = await caseService.create({
        title,
        fir_number: form.fir_number.trim() || undefined,
        officer_name: form.officer_name.trim() || undefined,
        notes: form.notes.trim() || undefined,
      });
      const id = String(created?.id || created?.case_id || '');
      if (!id) throw new Error('Case create returned no id');
      toast.success('Case created — opening Fusion Canvas');
      goCanvas(id, true);
    } catch (ex) {
      setErr(ex instanceof Error ? ex.message : 'Failed to create case');
      setBusy(false);
    }
  };

  const onOpenExisting = (e: React.FormEvent) => {
    e.preventDefault();
    if (guest) {
      setErr('Sign in to open a real case on Fusion Canvas.');
      return;
    }
    if (!selectedId) {
      setErr('Pick a case');
      return;
    }
    goCanvas(selectedId);
  };

  const selected = cases.find((c) => c.id === selectedId);
  const triggerLabel = selected
    ? `${selected.title}${selected.fir_number ? ` · ${selected.fir_number}` : ''}`
    : cases.length
      ? 'Select a case…'
      : 'No cases found';

  return (
    <div
      className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-start justify-center overflow-y-auto p-4 sm:items-center"
      role="dialog"
      aria-modal="true"
      aria-labelledby="fusion-case-entry-title"
      data-testid="fusion-case-entry-modal"
    >
      <div className="bg-card border border-border/60 rounded-xl w-full max-w-md shadow-2xl my-4 sm:my-8">
        <div className="h-1 w-full bg-primary rounded-t-xl" />
        <div className="p-5">
          <div className="flex items-start justify-between gap-3 mb-4">
            <div>
              <h2 id="fusion-case-entry-title" className="text-lg font-bold leading-tight">
                {mode === 'new' ? 'New Fusion case' : 'Open case on Fusion'}
              </h2>
              <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
                Same LE cases as CryptoFlow — work opens on Case Canvas, not the demo sandbox.
              </p>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="p-1.5 rounded-md text-muted-foreground hover:bg-muted hover:text-foreground"
              aria-label="Close"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          <div className="flex rounded-md border border-border overflow-hidden text-xs font-medium mb-4">
            <button
              type="button"
              onClick={() => { setMode('new'); setErr(null); setListOpen(false); }}
              data-testid="fusion-case-entry-new-tab"
              className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 transition-colors ${
                mode === 'new' ? 'bg-primary text-primary-foreground' : 'bg-secondary hover:bg-secondary/80'
              }`}
            >
              <Plus className="w-3.5 h-3.5" /> New case
            </button>
            <button
              type="button"
              onClick={() => { setMode('open'); setErr(null); }}
              data-testid="fusion-case-entry-open-tab"
              className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 transition-colors ${
                mode === 'open' ? 'bg-primary text-primary-foreground' : 'bg-secondary hover:bg-secondary/80'
              }`}
            >
              <FolderOpen className="w-3.5 h-3.5" /> Open case
            </button>
          </div>

          {guest && (
            <p className="text-xs text-amber-800 dark:text-amber-100 bg-amber-500/10 border border-amber-500/30 rounded-md px-3 py-2 mb-3">
              Guest demo cannot create or open live cases. Sign in, then use New case / Open case.
            </p>
          )}

          {mode === 'new' ? (
            <form onSubmit={onCreate} className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-muted-foreground mb-1">Case Title</label>
                <input
                  type="text"
                  required
                  disabled={guest || busy}
                  className="w-full bg-card border border-border rounded p-2 text-foreground text-sm"
                  placeholder="e.g. Frozen account — Rajesh Kumar"
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  data-testid="fusion-case-title"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-muted-foreground mb-1">FIR Number</label>
                <input
                  type="text"
                  disabled={guest || busy}
                  className="w-full bg-card border border-border rounded p-2 text-foreground text-sm"
                  placeholder="e.g. FIR-2026-001"
                  value={form.fir_number}
                  onChange={(e) => setForm({ ...form, fir_number: e.target.value })}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-muted-foreground mb-1">Investigating Officer</label>
                <input
                  type="text"
                  disabled={guest || busy}
                  className="w-full bg-card border border-border rounded p-2 text-foreground text-sm"
                  placeholder="Officer name"
                  value={form.officer_name}
                  onChange={(e) => setForm({ ...form, officer_name: e.target.value })}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-muted-foreground mb-1">Notes</label>
                <textarea
                  disabled={guest || busy}
                  className="w-full bg-card border border-border rounded p-2 text-foreground text-sm h-20"
                  placeholder="Initial details…"
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                />
              </div>
              {err && <p className="text-xs text-red-600">{err}</p>}
              <div className="flex justify-end gap-2 pt-1">
                <button
                  type="button"
                  onClick={onClose}
                  className="px-3 py-2 rounded-lg text-sm border border-border bg-secondary hover:bg-secondary/80"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={guest || busy}
                  data-testid="fusion-case-create-submit"
                  className="px-3 py-2 rounded-lg text-sm font-medium bg-primary text-primary-foreground hover:bg-brand-gold-hover disabled:opacity-50 flex items-center gap-1.5"
                >
                  {busy && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                  Create &amp; open Fusion
                </button>
              </div>
            </form>
          ) : (
            <form onSubmit={onOpenExisting} className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-muted-foreground mb-1" id="fusion-case-open-label">
                  Your cases
                </label>
                <div className="relative" ref={listboxRef}>
                  <button
                    type="button"
                    disabled={guest || busy || cases.length === 0}
                    aria-haspopup="listbox"
                    aria-expanded={listOpen}
                    aria-labelledby="fusion-case-open-label"
                    data-testid="fusion-case-open-select"
                    className="w-full flex items-center justify-between gap-2 bg-card border border-border rounded p-2 text-foreground text-sm text-left disabled:opacity-50"
                    onClick={() => setListOpen((v) => !v)}
                  >
                    <span className={`truncate ${selectedId ? '' : 'text-muted-foreground'}`}>{triggerLabel}</span>
                    <ChevronDown className={`w-4 h-4 shrink-0 text-muted-foreground transition-transform ${listOpen ? 'rotate-180' : ''}`} />
                  </button>
                  {listOpen && cases.length > 0 && (
                    <ul
                      role="listbox"
                      aria-labelledby="fusion-case-open-label"
                      data-testid="fusion-case-open-list"
                      className="absolute left-0 right-0 top-full z-20 mt-1 max-h-56 overflow-y-auto rounded-md border border-border bg-card shadow-lg styled-scrollbar"
                    >
                      {cases.map((c) => {
                        const label = `${c.title}${c.fir_number ? ` · ${c.fir_number}` : ''}`;
                        const active = c.id === selectedId;
                        return (
                          <li key={c.id} role="option" aria-selected={active}>
                            <button
                              type="button"
                              className={`w-full text-left px-3 py-2 text-sm hover:bg-muted/80 ${
                                active ? 'bg-muted font-medium' : ''
                              }`}
                              onClick={() => {
                                setSelectedId(c.id);
                                setListOpen(false);
                                setErr(null);
                              }}
                            >
                              <span className="block truncate">{label}</span>
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </div>
              </div>
              {err && <p className="text-xs text-red-600">{err}</p>}
              <div className="flex justify-end gap-2 pt-1">
                <button
                  type="button"
                  onClick={onClose}
                  className="px-3 py-2 rounded-lg text-sm border border-border bg-secondary hover:bg-secondary/80"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={guest || busy || !selectedId}
                  data-testid="fusion-case-open-submit"
                  className="px-3 py-2 rounded-lg text-sm font-medium bg-primary text-primary-foreground hover:bg-brand-gold-hover disabled:opacity-50"
                >
                  Open Fusion Canvas
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}